// 入力フォームの初期値 (initialValues属性にセットする)
import { PagePath } from 'constants/pagePath';
import { useFormik } from 'formik';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ToastMessageLevel } from 'views/mutual/toast';
import { useToast } from 'views/mutual/toast/hooks';
import { SendSMSParam } from 'views/templates/sendSMS';
import { authenticationCodeValidation } from 'views/utils/commonValidation';
import { useReissuePasswordMutation, useSendSmsMutation, useSmsCertificateMutation } from 'views/utils/graphql/graphql';
import useOnceSubmit from 'views/utils/useOnceSubmit';
import { usePersonalAccountEstablishStore } from 'views/templates/personalAccountOpen/hooks';
import { TemplateProps } from '../common';
import { PageStep } from 'views/pages/accountInfo/hooks';
import { useGlobalToastHandler } from 'views/mutual/globalToast/hooks';
import { DateFormatType, formatDate } from 'views/utils/converter';
import {
  findOccupationId,
  findDomesticRealEstatePurposeId,
  findPersonalBusinessContentId,
  ChangeAccountInfoExecuteType,
  findInvestorCategoryInfoId,
  InvestorCategoryInfo,
  FatcaStatus,
} from 'views/utils/enums';
import { ChangeAccountInfoMutationVariables, useChangeAccountInfoMutation } from 'views/utils/graphql/graphql';

export type LoginParamType = {
  account: string;
  Email: string;
};

export const useUpdateMail = ({isUpdate, setPageStep}: TemplateProps) => {
  const [provisionalToken, setProvisionalToken] = useState<string>('');
  const [sendSMSData, sendSMS] = useSendSmsMutation();
  const { toastProps, showToast } = useToast();
  const { add, param, clear } = usePersonalAccountEstablishStore();
  const { onSendSMS } = useSendEmail(provisionalToken, setProvisionalToken, showToast);
  const certificatePage = useCertificatePage(provisionalToken, showToast, setPageStep);

  useEffect(() => {
  const id = setTimeout(() => {
    void sendEmail();
  }, 0);
  return () => clearTimeout(id);
}, []);

const sendEmail = async () => {
  const ret = await sendSMS({
    input: {
      Type: 5,
      Email: param.customerMailAddress,
    },
  });

    if (ret.data && ret.data?.SendSMS?.Code !== 0) {
    showToast(ret.data?.SendSMS?.Message ?? '認証コードの送信に失敗しました', ToastMessageLevel.Error);
  } else if (ret.data?.SendSMS?.Code === 0) {
    setProvisionalToken(ret.data.SendSMS.ProvisionalToken!);
  }
}



  const goBackPage = () => {
    setPageStep && setPageStep(PageStep.CONFIRM);
  };

  return { toastProps, certificatePage: { onSendSMS, ...certificatePage }, goBackPage };
};

const useSendEmail = (
  initialProvisionalToken: string,
  setProvisionalToken: React.Dispatch<React.SetStateAction<string>>,
  onShowToast: (label: string, mesageLevel: ToastMessageLevel) => void,
) => {
  const [sendSMSData, sendSMS] = useSendSmsMutation();
  const { add, param } = usePersonalAccountEstablishStore();
  const [newMailAddress] = useState(param.customerMailAddress);
  const onSendSMS = useOnceSubmit<void>(
    {
      onSubmit: async () => {
        const res = await sendSMS({
          input: {
            Type: 5,
            Email: newMailAddress,
          },
        });

        if (res.data && res.data?.SendSMS?.Code !== 0) {
          onShowToast(res.data?.SendSMS?.Message ?? '認証コードの送信に失敗しました', ToastMessageLevel.Error);
        } else if (res.data?.SendSMS?.Code === 0) {
          setProvisionalToken(res.data.SendSMS.ProvisionalToken!);
          onShowToast('認証コードを送信しました', ToastMessageLevel.Info);
        }
      },
    },
    [sendSMS, initialProvisionalToken, onShowToast, setProvisionalToken],
  );

  return {
    onSendSMS,
  };
};

const useCertificatePage = (
  provisionalToken: string,
  showToast: (label: string, mesageLevel: ToastMessageLevel) => void,
  setPageStep?: React.Dispatch<React.SetStateAction<PageStep>>,
) => {
  const [smsCertificatelData, smsCertificate] = useSmsCertificateMutation();
  const { add, param, clear } = usePersonalAccountEstablishStore();
  const { showError } = useGlobalToastHandler();
  const [_changeAccountResponse, changeAccount] = useChangeAccountInfoMutation();

  const validate = (values: SendSMSParam) => {
    const errors: Partial<{
      authenticationCode: string;
    }> = {};

    const codeError = authenticationCodeValidation(values.authenticationCode);
    if (codeError) {
      errors.authenticationCode = codeError;
    }

    return errors;
  };

  const smsCertificateSubmit = useOnceSubmit<SendSMSParam>(
    {
      onSubmit: async (values) => {
        const ret = await smsCertificate({
          input: {
            Type: 5,
            ProvisionalToken: provisionalToken,
            AuthenticationCode: values.authenticationCode,
          },
        });

        if (ret.data && ret.data.SMSCertificate?.Code !== 0) {
          showToast(ret.data.SMSCertificate?.Message ?? '認証に失敗しました', ToastMessageLevel.Error);
        } else{
          await executeUpdateAccount();
        }
      },
    },
    [smsCertificate, showToast],
  );

  const executeUpdateAccount = async () => {
        const data: ChangeAccountInfoMutationVariables = {
          input: {
            HaveEKYCDocument: param.identificationQuestion,
            CanTakePicture: param.selfieQuestion,
            ConfirmResponsibility: param.confirmResponsibility,
            DealerFirstName: param.customerFirstName,
            DealerLastName: param.customerLastName,
            DealerFirstNameKana: param.customerFirstNameKana,
            DealerLastNameKana: param.customerLastNameKana,
            PostCode: param.customerPostCode,
            Prefecture: param.customerPrefecture,
            CityCountry: param.customerCityCountry,
            TownStreetAddress: param.customerTownStreetAddress,
            BuildingNameAndRoomNumber: param.customerBuildingNameAndRoomNumber,
            DealerMobilePhoneNumber: param.customerMobilePhone,
            ContactPhoneNumber: param.customerHomePhone,
            ContactFaxNumber: param.customerFaxNumber,
            DealerMailAddress: param.customerMailAddress,
            Occupation: param.occupation.filter((x) => x.checked).map((x) => findOccupationId(x.label) ?? null),
            OccupationOtherText: param.occupationOtherText,
            PersonalMainBusinessContent: findPersonalBusinessContentId(param.customerMainBusinessContent),
            PersonalMainBusinessContentOtherText: param.customerMainBusinessContentOtherText,
            PersonalOtherBusinessContent: findPersonalBusinessContentId(param.customerOtherBusinessContent),
            PersonalOtherBusinessContentOtherText: param.customerOtherBusinessContentOtherText,
            DomesticRealEstatePurpose: param.purpose
              .filter((x) => x.checked)
              .map((x) => findDomesticRealEstatePurposeId(x.label) ?? null),
            DomesticRealEstatePurposeOtherText: param.purposeOtherText,
            Fatca: param.fatca,
            ImportantPublicWorker: param.importantPublicWorker,
            TransferBankName: param.bankName,
            TransferBranchName: param.branchName,
            TransferAccountType: param.accountType,
            TransferAccountNumber: param.accountNumber,
            TransferAccountHolderName: param.accountHolderName,
            InvoiceNumber: param.invoiceNumber,
            HasNoInvoiceNumber: param.hasNoInvoiceNumber,
            TwoFactorAuthenticationType: param.authMode,
            //個人口座は固定で上記のいずれでもないが入っていそうなので入れる
            InvestorCategoryInfo: [findInvestorCategoryInfoId(InvestorCategoryInfo.Other)!],
          },
        };

        const changeAccountData = await changeAccount(data);
        if (!changeAccountData?.data) return;

        if (changeAccountData.data.ChangeAccountInfo?.Code === 0) {
          setPageStep && setPageStep(PageStep.COMPLETE);
        } else {
          showError(changeAccountData.data.ChangeAccountInfo?.Message);
        }
      }


  const formik = useFormik<SendSMSParam>({
    initialValues: {
      authenticationCode: '',
    },
    onSubmit: smsCertificateSubmit,
    validate,
    validateOnBlur: false,
    validateOnChange: false,
  });

  useEffect(() => {
    formik.setFieldValue('authenticationCode', '');
  }, []);

  const maskMailAddress = (addr?: string | null) => {
  if (!addr) return '';
  const head = addr.slice(0, 4);
  const tail = addr.length >= 4 ? addr.slice(-4) : '';
  return `メールアドレス${head}********${tail}宛に送信された認証コードを入力してください`;
};

  return { formik, text: maskMailAddress(param.customerMailAddress), isUpdate: false };
};
